// INSIDO (16-mensagens-de-voz): voice note recorder hook — microphone
// permission, MediaRecorder (Opus/WebM preferred), live AnalyserNode levels,
// elapsed clock, 5-minute cap with a warning at 4:30, and on stop: decode the
// recording to compute exact duration + peaks and hand back a named File.
import { batch, createSignal, onCleanup } from "solid-js";

import { VOICE_MAX_MS, VOICE_WARN_MS } from "./duration";
import { buildVoiceFilename, pickRecorderMime } from "./voiceFile";
import {
  VOICE_BARS,
  levelsToBytes,
  normalise,
  peaksFromAudioBuffer,
  resampleLevels,
  rmsLevel,
} from "./waveform";

/** Recorder lifecycle */
export type VoiceRecorderStatus =
  | "idle"
  | "starting"
  | "recording"
  | "processing";

/** `hold`: release sends; `locked`: hands-free, explicit send / cancel */
export type VoiceRecorderMode = "hold" | "locked";

/** Number of live bars kept for the recording strip */
export const LIVE_BARS = 40;

const TICK_MS = 80;

export interface VoiceRecorderOptions<T> {
  /** Captured when recording starts and passed back with the file */
  context: () => T;
  /** A finished recording, ready to upload */
  onRecorded: (file: File, context: T) => void | Promise<void>;
  /** Microphone permission denied */
  onPermissionDenied: () => void;
  /** Any other failure */
  onError: (error: unknown) => void;
  /** Reached 4:30 */
  onLimitWarning: () => void;
}

/**
 * Whether this browser can record voice notes at all
 * @returns Support flag
 */
export function isVoiceRecordingSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof window.AudioContext !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

/**
 * Create a voice note recorder bound to the current owner (cancels on cleanup)
 * @param options Callbacks
 * @returns Recorder state and controls
 */
export function createVoiceRecorder<T>(options: VoiceRecorderOptions<T>) {
  const [status, setStatus] = createSignal<VoiceRecorderStatus>("idle");
  const [mode, setMode] = createSignal<VoiceRecorderMode>("hold");
  const [elapsed, setElapsed] = createSignal(0);
  const [levels, setLevels] = createSignal<number[]>([]);
  const [dragX, setDragX] = createSignal(0);
  const [nearLimit, setNearLimit] = createSignal(false);

  let session = 0;
  let stream: MediaStream | undefined;
  let recorder: MediaRecorder | undefined;
  let audioContext: AudioContext | undefined;
  let analyser: AnalyserNode | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  let chunks: Blob[] = [];
  let startedAt = 0;
  let startedAtWall = 0;
  let extension = "webm";
  let context: T;
  let history: number[] = [];

  /** Release microphone, analyser and clock */
  function releaseMedia() {
    if (timer) clearInterval(timer);
    timer = undefined;
    stream?.getTracks().forEach((track) => track.stop());
    stream = undefined;
    analyser = undefined;
    audioContext?.close().catch(() => {});
    audioContext = undefined;
  }

  /** Sample the analyser and advance the clock */
  function tick() {
    const now = performance.now() - startedAt;
    if (analyser) {
      const data = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(data);
      // speech RMS rarely exceeds ~0.3 → boost so the bars are readable
      history = [...history, Math.min(1, rmsLevel(data) * 3.2)].slice(-400);
      setLevels(history.slice(-LIVE_BARS));
    }
    setElapsed(now);

    if (now >= VOICE_WARN_MS && !nearLimit()) {
      setNearLimit(true);
      options.onLimitWarning();
    }
    if (now >= VOICE_MAX_MS) stop(true);
  }

  /**
   * Ask for the microphone and start recording (hold mode)
   * @param initialMode Start directly hands-free (keyboard)
   */
  async function start(initialMode: VoiceRecorderMode = "hold") {
    if (status() !== "idle") return;
    const mine = ++session;
    context = options.context();
    history = [];
    batch(() => {
      setStatus("starting");
      setMode(initialMode);
      setElapsed(0);
      setLevels([]);
      setDragX(0);
      setNearLimit(false);
    });

    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (error) {
      if (mine !== session) return;
      setStatus("idle");
      const name = (error as { name?: string } | undefined)?.name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        options.onPermissionDenied();
      } else {
        options.onError(error);
      }
      return;
    }

    // cancelled (or unmounted) while the permission prompt was open
    if (mine !== session) {
      media.getTracks().forEach((track) => track.stop());
      return;
    }

    try {
      stream = media;
      const picked = pickRecorderMime((type) =>
        MediaRecorder.isTypeSupported(type),
      );
      extension = picked.extension;
      recorder = new MediaRecorder(
        media,
        picked.mimeType ? { mimeType: picked.mimeType } : undefined,
      );
      chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data);
      };
      recorder.start(250);

      audioContext = new AudioContext();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      audioContext.createMediaStreamSource(media).connect(analyser);

      startedAt = performance.now();
      startedAtWall = Date.now();
      timer = setInterval(tick, TICK_MS);
      setStatus("recording");
    } catch (error) {
      releaseMedia();
      setStatus("idle");
      options.onError(error);
    }
  }

  /**
   * Decode the recording for exact duration + peaks
   * @param blob Recording
   * @param fallbackMs Clock duration if decoding fails
   */
  async function analyse(
    blob: Blob,
    fallbackMs: number,
  ): Promise<{ durationMs: number; peaks: number[] }> {
    const fallback = {
      durationMs: fallbackMs,
      peaks: normalise(resampleLevels(history, VOICE_BARS)),
    };
    const ctx = new AudioContext();
    try {
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      if (!Number.isFinite(buffer.duration) || buffer.duration <= 0) {
        return fallback;
      }
      return {
        durationMs: buffer.duration * 1000,
        peaks: peaksFromAudioBuffer(buffer, VOICE_BARS),
      };
    } catch {
      return fallback;
    } finally {
      ctx.close().catch(() => {});
    }
  }

  /**
   * Stop recording
   * @param send Whether to send (true) or discard (false)
   */
  function stop(send: boolean) {
    const current = status();
    if (current === "starting") {
      // released while the permission prompt was up: keep going hands-free
      if (send) setMode("locked");
      else cancel();
      return;
    }
    if (current !== "recording" || !recorder) return;

    const rec = recorder;
    recorder = undefined;
    const clockMs = performance.now() - startedAt;
    const recordedChunks = chunks;
    const timestamp = startedAtWall;
    const ext = extension;
    const ctx = context;

    const finished = new Promise<Blob>((resolve) => {
      rec.onstop = () =>
        resolve(
          new Blob(recordedChunks, {
            type: rec.mimeType || "audio/webm",
          }),
        );
    });
    rec.stop();
    releaseMedia();

    if (!send) {
      setStatus("idle");
      return;
    }

    setStatus("processing");
    finished
      .then(async (blob) => {
        const { durationMs, peaks } = await analyse(blob, clockMs);
        const type = blob.type.split(";")[0] || "audio/webm";
        const file = new File(
          [blob],
          buildVoiceFilename({
            timestamp,
            durationMs,
            peaks: levelsToBytes(peaks),
            extension: ext,
          }),
          { type },
        );
        await options.onRecorded(file, ctx);
      })
      .catch((error) => options.onError(error))
      .finally(() => setStatus("idle"));
  }

  /** Discard the current recording (or a pending start) */
  function cancel() {
    const current = status();
    if (current === "starting") {
      session++;
      setStatus("idle");
      return;
    }
    if (current === "recording") stop(false);
  }

  /** Switch to hands-free mode */
  function lock() {
    if (status() === "starting" || status() === "recording") setMode("locked");
  }

  onCleanup(() => {
    cancel();
    releaseMedia();
  });

  return {
    status,
    mode,
    elapsed,
    levels,
    dragX,
    setDragX,
    nearLimit,
    start,
    stop,
    cancel,
    lock,
    /** Whether a recording is in progress (any non-idle state) */
    active: () => status() !== "idle",
  };
}

export type VoiceRecorder = ReturnType<typeof createVoiceRecorder>;
